import { Component, Input, OnChanges, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CsvService, Group, StoreService, PopUpService, Course } from '../../core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { LicensesActivityReportService } from '../../licenses/services/licenses-activity-report.service';

@Component({
  selector: 'lms-group-log',
  templateUrl: './group-log.component.html',
  styleUrls: ['./group-log.component.scss']
})
export class GroupLogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public group: Group;
  @Input() public course: Course;
  @Input() public options: any;
  @Input() public listId: string;
  public start: any;
  public end: any;
  public originalEnd: any;
  public today: any;
  public rangeConfig: any;
  public extensions = [];
  public started = false;

  public licenseResumes: any[];
  public gameColumns: any[];
  public filters = {};
  public listOptions: any;
  public shouldShowRatio: boolean;
  public defaultOptions = {
    filters: this.filters
  };
  public ratioFinished = 0;
  public ratioBonused = 0;
  public bonusedStudents = 0;

  public selectedDate;
  private selectedMillis;

  public data = [];
  public dataSource: any;
  public labels: string[] = [
    this.translate.instant('reports.charts.notStarted'),
    this.translate.instant('reports.charts.completed'),
    this.translate.instant('reports.charts.playingLately'),
    this.translate.instant('reports.charts.notPlayingLately')
  ];
  public legendColorClasses = ['chart-red', 'chart-blue', 'chart-lightgreen', 'chart-darkgreen'];
  public colors: { backgroundColor: string[] } = {
    backgroundColor: ['#e65100', '#009dda', '#33c9c0', '#18544e']
  };
  public chartOptions = { height: 200 };

  public format = DateTime.DATETIME_MED_WITH_SECONDS;
  public listDataSubscription: Subscription;
  public toolbarConfig: any;

  constructor(
    private translate: TranslateService,
    private csvService: CsvService,
    private licensesActivityReportService: LicensesActivityReportService,
    private popupService: PopUpService
  ) {}

  public ngOnInit(): void {
    this.toolbarConfig = {
      list: {
        listId: this.listId,
        download: true,
        filterRow: true,
        columnChooser: false,
        skeletor: true
      }
    };
    this.listDataSubscription = StoreService.get(this.listId).subscribe((data) => {
      this.dataSource = data;
      if (data && data.docs.length > 0) {
        this.setCollection(data);
      }
    });
  }

  public ngOnDestroy(): void {
    this.listDataSubscription.unsubscribe();
  }

  public ngOnChanges(): void {
    this.listOptions = Object.assign({}, this.defaultOptions, this.options);
    this.setReportDates();
    if (this.group) {
      this.onFinish(this.rangeConfig, false);
    }
  }

  setReportDates() {
    const start = DateTime.fromJSDate(new Date(this.group.startDate), {
      zone: this.group.timezone
    }).endOf('day');
    const { endDate, originalEndDate = endDate } = this.group;
    const end = DateTime.fromJSDate(endDate ? new Date(endDate) : new Date(), {
      zone: this.group.timezone
    }).endOf('day');
    const originalEnd = DateTime.fromJSDate(originalEndDate ? new Date(originalEndDate) : new Date(), {
      zone: this.group.timezone
    }).endOf('day');
    this.originalEnd = originalEnd.toMillis();

    let diffDays = end.diff(start, 'day').days;
    while (diffDays > 15) {
      diffDays /= 2;
    }

    let fundaeTagHack = 0;
    if (this.group.hasBonus) {
      // Move Fundae tag if daylight saving time change date
      // is between start date and fundae date
      const utcStart = start.toUTC();
      const utcOriginalEnd = originalEnd.toUTC();
      if (utcStart.hour < utcOriginalEnd.hour) {
        fundaeTagHack = 24 * 60 * 60 * 1000;
      } else if (utcStart.hour > utcOriginalEnd.hour) {
        fundaeTagHack = -24 * 60 * 60 * 1000;
      } else {
        fundaeTagHack = 0;
      }
    }

    this.start = start.toMillis();
    this.end = end.toMillis();
    this.today = DateTime.fromJSDate(new Date(), { zone: this.group.timezone }).endOf('day').toMillis();

    const from = this.group.hasBonus
      ? Math.min(this.today, this.originalEnd, this.end)
      : Math.min(this.today, this.end);

    this.extensions = [];
    this.rangeConfig = {
      keyboard: true,
      grid: false,
      // grid_num: diffDays,
      min: this.start,
      max: this.end,
      from,
      step: 24 * 60 * 60 * 1000,
      extra_classes: 'margin-bottom',
      prettify: (num) => this.prettify(num),
      onChange: (data) => {
        this.selectedMillis = data.from;
      },
      onStart: (data) => {
        if (this.group.hasBonus) {
          const left = ((this.originalEnd + fundaeTagHack - this.start) / (this.end - this.start)) * 100;
          data.slider.append(`<span class="irs-mark" style="left: calc(${left}% - 9px)">Fundae</span>`);
        }
        this.started = true;
      },
      onFinish: (event) => this.onFinish(event)
    };
  }

  getValue(resume, field) {
    // eslint-disable-next-line no-prototype-builtins
    return resume && resume.values && resume.values.hasOwnProperty(field.name) ? resume.values[field.name] : '';
  }

  setCollection(licenseResumes) {
    this.licenseResumes = licenseResumes;
    const filters = { ...this.filters };

    this.licensesActivityReportService.getReportData(filters).then((reportData) => {
      this.data = [
        reportData.notStartedYet,
        reportData.completed,
        reportData.playingLately,
        reportData.notPlayingLately
      ];
      this.bonusedStudents = reportData.bonusedStudents;
      this.ratioFinished = reportData.ratioFinished;
      this.ratioBonused = reportData.ratioBonused;
    });
  }

  secondsToHms(totalSeconds) {
    const tmp = Number(totalSeconds);
    const h = Math.floor(tmp / 3600);
    const m = Math.floor((tmp % 3600) / 60);
    const s = Math.floor((tmp % 3600) % 60);

    const hDisplay = h > 0 ? h + 'h' : '';
    const mDisplay = m > 0 ? m + 'm' : '';
    const sDisplay = s > 0 ? s + 's' : '';
    let ret = '0s';
    if (hDisplay || mDisplay || sDisplay) {
      ret = hDisplay + mDisplay + sDisplay;
    }
    return ret;
  }

  download() {
    if (this.group.hasBonus) {
      const originalEndDateString = DateTime.fromMillis(this.originalEnd, {
        zone: this.group.timezone
      }).toLocaleString();
      const selectedDateTime = DateTime.fromMillis(this.selectedMillis, {
        zone: this.group.timezone
      });
      const fundaeDateTime = DateTime.fromMillis(this.originalEnd, { zone: this.group.timezone });
      const selectedDay = selectedDateTime.day;
      const selectedMonth = selectedDateTime.month;
      const selectedYear = selectedDateTime.year;
      const fundaeDay = fundaeDateTime.day;
      const fundaeMonth = fundaeDateTime.month;
      const fundaeYear = fundaeDateTime.year;

      if (selectedDay !== fundaeDay || selectedMonth !== fundaeMonth || selectedYear !== fundaeYear) {
        this.popupService
          .confirmWarning({
            text: this.translate.instant('groups.bonus.warning.detail', { originalEndDateString }),
            title: this.translate.instant('groups.bonus.warning.title')
          })
          .then(() => {
            this.downloadCsv();
          })
          .catch(() => {});
      } else {
        this.downloadCsv();
      }
    } else {
      this.downloadCsv();
    }
  }

  downloadCsv() {
    const rows = this.licenseResumes.map((license) => {
      const privateRows = [];
      privateRows['userName'] = license.user.name;
      privateRows['userSurname'] = license.user.surname;
      privateRows['userEmail'] = license.user.email;
      privateRows['lang'] = license.lang;
      if (license.values) {
        this.gameColumns.map((field) => {
          privateRows[field.name] = this.getValue(license, field);
        });
        privateRows['startDate'] = license.values.startDate ? new Date(license.values.startDate) : '';
        privateRows['endDate'] = license.values.endDate ? new Date(license.values.endDate) : '';

        privateRows['timeProgress'] = license.values.durationSeconds
          ? this.secondsToHms(license.values.durationSeconds || 0)
          : '0s';
        privateRows['gameProgress'] = license.values.gameProgress
          ? Math.round(license.values.gameProgress || 0) + '%'
          : 0 + '%';
        privateRows['bonus'] = license.bonus.isReached;
      }

      return privateRows;
    });
    this.csvService.dataToCsv(rows, this.group.name, {
      showLabels: true,
      headers: Object.keys(rows[0])
    });
  }

  prettify(num) {
    const dateString = DateTime.fromMillis(parseInt(num, 10), {
      zone: this.group.timezone
    }).toLocaleString();
    const selectedDateTime = DateTime.fromMillis(num);
    const fundaeDateTime = DateTime.fromMillis(this.originalEnd, { zone: this.group.timezone });
    const selectedDay = selectedDateTime.day;
    const selectedMonth = selectedDateTime.month;
    const selectedYear = selectedDateTime.year;
    const fundaeDay = fundaeDateTime.day;
    const fundaeMonth = fundaeDateTime.month;
    const fundaeYear = fundaeDateTime.year;
    if (
      this.group.hasBonus &&
      selectedDay === fundaeDay &&
      selectedMonth === fundaeMonth &&
      selectedYear === fundaeYear
    ) {
      return `<span style="font-size: 16px">${dateString}</span>`;
    } else {
      return `<span>${dateString}</span>`;
    }
  }

  onFinish(event, propagate = true) {
    // 12 extra hours to request students progress for finished groups
    const delta = event.from >= this.end ? 12 * 60 * 60 * 1000 : 0;
    const date = DateTime.fromMillis(event.from + delta + 1, {
      zone: this.group.timezone
    });
    // Reset filters with updated date and groupId
    this.filters = { date: date.toUTC(), groupId: this.group.id };
    this.listOptions.filters = this.filters;

    if (propagate) {
      this.dataSource.refreshData({ filters: this.filters, page: 1 });
    }
    this.selectedDate = date;
    this.selectedMillis = event.from;

    this.shouldShowRatio = date.toJSDate().getTime() >= Math.min(this.today, new Date(this.group.endDate).getTime());
  }

  /**
   * Set the toolbar of table
   * @param hideSearch
   */
  public setToolbarTable(hideSearch: boolean): void {
    this.toolbarConfig = {
      ...this.toolbarConfig,
      list: {
        ...this.toolbarConfig.list,
        download: !hideSearch,
        filterRow: !hideSearch,
        hideSearch: hideSearch,
        hideItemsSelector: hideSearch
      }
    };
  }
}
